// GeneralizedForm.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStepBackward, faTrash, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import { Modal, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { apiUrl, hostUrl } from './config';
import './GeneralizedForm.css';

const GeneralizedForm = ({ formConfigUrl }) => {
    const { resource, id } = useParams();
    const navigate = useNavigate();
    const [formConfig, setFormConfig] = useState([]);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    let local_id = '';
    if (id === undefined) {
        local_id = 'new';
    }

    const getAccessToken = () => localStorage.getItem('access_token');
    const getRefreshToken = () => localStorage.getItem('refresh_token');
    const setAccessToken = (token) => localStorage.setItem('access_token', token);

    const refreshAccessToken = async () => {
        try {
            const response = await axios.post(`${apiUrl}/token/refresh/`, {
                refresh: getRefreshToken(),
            });
            setAccessToken(response.data.access);
            return response.data.access;
        } catch (error) {
            console.error('Failed to refresh access token', error);
            return null;
        }
    };

    const axiosInstance = axios.create();

    axiosInstance.interceptors.request.use(
        async (config) => {
            let token = getAccessToken();
            if (token) {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;

                if (decodedToken.exp < currentTime) {
                    token = await refreshAccessToken();
                }
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                const newToken = await refreshAccessToken();
                if (newToken) {
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return axiosInstance(originalRequest);
                }
            }
            return Promise.reject(error);
        }
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const configResponse = await axiosInstance.get(`${formConfigUrl}/${resource}/`);
                const configData = configResponse.data;
                const enhancedFormConfig = configData.map((field) => ({
                    ...field,
                    name: field.label.toLowerCase().replace(/\s+/g, '_'),
                }));
                setFormConfig(enhancedFormConfig);

                if (id && id !== 'new') {
                    const dataResponse = await axiosInstance.get(`${hostUrl}/${resource}/${id}/?format=json`);
                    const data = dataResponse.data;
                    setFormData(formatDateTimeLocal(data, enhancedFormConfig));
                }
                setLoading(false);
            } catch (error) {
                console.error('Error:', error);
                setError(error.message);
                setShowErrorModal(true);
                setLoading(false);
            }
        };

        fetchData();
    }, [formConfigUrl, resource, id]);

    const formatDateTimeLocal = (data, formConfig) => {
        const formattedData = { ...data };
        formConfig.forEach((field) => {
            if (field.type === 'datetime-local' && formattedData[field.name]) {
                const date = new Date(formattedData[field.name]);
                const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                formattedData[field.name] = formattedDate;
            }
        });
        return formattedData;
    };

    const validateField = (fieldName, value, fieldConfig) => {
        if (fieldConfig.required === 'True' && !value) {
            return `${formatLabel(fieldConfig.label)} is required`;
        }
        return null;
    };

    const handleChange = (e) => {
        const { name, value, type, checked, multiple, selectedOptions } = e.target;
        let newValue;
        
        if (type === 'checkbox') {
            newValue = checked;
        } else if (multiple) {
            newValue = Array.from(selectedOptions).map((option) => option.value);
        } else {
            newValue = value;
        }

        setFormData({
            ...formData,
            [name]: newValue,
        });

        // Clear field error on change
        if (fieldErrors[name]) {
            setFieldErrors({
                ...fieldErrors,
                [name]: null,
            });
        }
    };

    const validateForm = () => {
        const errors = {};
        formConfig.forEach((field) => {
            const error = validateField(field.name, formData[field.name], field);
            if (error) {
                errors[field.name] = error;
            }
        });
        return errors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setSubmitting(true);
        const method = local_id === 'new' ? 'POST' : 'PUT';
        const url = local_id === 'new' ? `${hostUrl}/${resource}/` : `${hostUrl}/${resource}/${id}/`;

        axiosInstance({
            method: method,
            url: url,
            data: formData,
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((response) => {
                setSubmitting(false);
                setModalMessage(`${local_id === 'new' ? 'Created' : 'Updated'} successfully!`);
                setShowSuccessModal(true);
                setFieldErrors({});
            })
            .catch((error) => {
                setSubmitting(false);
                console.error('Error submitting form:', error);
                
                // Handle field-specific validation errors
                if (error.response?.data && typeof error.response.data === 'object') {
                    const responseErrors = error.response.data;
                    
                    // Check if it's a field validation error object
                    if (!responseErrors.detail && Object.keys(responseErrors).length > 0) {
                        // Set field errors
                        setFieldErrors(responseErrors);
                        
                        // Create a summary message
                        const errorMessages = Object.entries(responseErrors)
                            .map(([field, messages]) => {
                                const msgArray = Array.isArray(messages) ? messages : [messages];
                                return `${formatLabel(field)}: ${msgArray.join(', ')}`;
                            })
                            .join('\n');
                        setModalMessage(`Validation errors:\n${errorMessages}`);
                    } else {
                        // Generic error with detail
                        const errorMsg = responseErrors.detail || error.message;
                        setModalMessage(`Error: ${errorMsg}`);
                    }
                } else {
                    setModalMessage(`Error: ${error.message}`);
                }
                
                setShowErrorModal(true);
            });
    };

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
            setSubmitting(true);
            axiosInstance({
                method: 'DELETE',
                url: `${hostUrl}/${resource}/${id}/`,
            })
                .then((response) => {
                    setSubmitting(false);
                    setModalMessage('Item deleted successfully');
                    setShowSuccessModal(true);
                })
                .catch((error) => {
                    setSubmitting(false);
                    console.error('Error deleting item:', error);
                    const errorMsg = error.response?.data?.detail || error.message;
                    setModalMessage(`Error: ${errorMsg}`);
                    setShowErrorModal(true);
                });
        }
    };

    const formatLabel = (label) => label.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    const renderField = (field) => {
        const fieldError = fieldErrors[field.name];
        const isInvalid = !!fieldError;

        if (field.choices) {
            const isMultiple = field.multiple === 'true' || field.multiple === 'True';
            const isRequired = field.required === 'true' || field.required === 'True';
            return (
                <>
                    <select
                        multiple={isMultiple}
                        className={`form-select ${isInvalid ? 'is-invalid' : ''}`}
                        name={field.name}
                        value={formData[field.name] || (isMultiple ? [] : '')}
                        onChange={handleChange}
                        required={isRequired}
                        size={isMultiple ? '4' : undefined}
                    >
                        {!isMultiple && <option value="">Select...</option>}
                        {field.choices.map((choice, index) => (
                            <option key={index} value={choice[1]}>
                                {choice[0]}
                            </option>
                        ))}
                    </select>
                    {isInvalid && <div className="invalid-feedback d-block">{fieldError}</div>}
                </>
            );
        }

        const isRequired = field.required === 'true' || field.required === 'True';
        
        switch (field.type) {
            case 'textarea':
                return (
                    <>
                        <textarea
                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={handleChange}
                            required={isRequired}
                            rows="4"
                        />
                        {isInvalid && <div className="invalid-feedback d-block">{fieldError}</div>}
                    </>
                );
            case 'checkbox':
                return (
                    <div className="form-check">
                        <input
                            className={`form-check-input ${isInvalid ? 'is-invalid' : ''}`}
                            type="checkbox"
                            name={field.name}
                            id={field.name}
                            checked={!!formData[field.name]}
                            onChange={handleChange}
                            required={isRequired}
                        />
                        <label className="form-check-label" htmlFor={field.name}>
                            {formatLabel(field.label)}
                        </label>
                        {isInvalid && <div className="invalid-feedback d-block">{fieldError}</div>}
                    </div>
                );
            case 'datetime-local':
                return (
                    <>
                        <input
                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                            type="datetime-local"
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={handleChange}
                            required={isRequired}
                        />
                        {isInvalid && <div className="invalid-feedback d-block">{fieldError}</div>}
                    </>
                );
            default:
                // Special handling for password field
                const inputType = field.name === 'password' ? 'password' : field.type;
                return (
                    <>
                        <input
                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                            type={inputType}
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={handleChange}
                            required={isRequired}
                            placeholder={`Enter ${formatLabel(field.label).toLowerCase()}`}
                        />
                        {isInvalid && <div className="invalid-feedback d-block">{fieldError}</div>}
                    </>
                );
        }
    };

    return (
        <div className="d-flex flex-row">
            <Header />
            <main style={{ marginTop: '58px' }} className="flex-grow-1">
                <div className="d-flex">
                    <div className="content flex-grow-1 mt-4">
                        <div className="container mt-4 pb-5">
                            {/* Header Section */}
                            <div className="form-header mb-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <button 
                                        className="btn btn-outline-secondary" 
                                        onClick={() => navigate(`/${resource}`)}
                                    >
                                        <FontAwesomeIcon icon={faStepBackward} /> Back
                                    </button>
                                    <h2 className="form-title">
                                        {id === 'new' ? `New ${formatLabel(resource)}` : `Edit ${formatLabel(resource)}`}
                                    </h2>
                                    {id !== 'new' && (
                                        <button 
                                            className="btn btn-danger" 
                                            onClick={handleDelete}
                                            disabled={submitting}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Form Section */}
                            <form onSubmit={handleSubmit} className="generalized-form">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary" />
                                        <p className="mt-3 text-muted">Loading form...</p>
                                    </div>
                                ) : error ? (
                                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                        <strong>Error:</strong> {error}
                                        <button 
                                            type="button" 
                                            className="btn-close" 
                                            onClick={() => setError(null)}
                                        ></button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="form-fields">
                                            <div className="row">
                                                {formConfig
                                                    // Filter out username and password fields when updating (not creating new user)
                                                    .filter(field => {
                                                        if (local_id !== 'new' && ['username', 'password'].includes(field.name)) {
                                                            return false;
                                                        }
                                                        return true;
                                                    })
                                                    .map((field, index) => (
                                                    <div className="col-lg-6 mb-4" key={index}>
                                                        <div className="form-group">
                                                            <label className="form-label fw-500">
                                                                {formatLabel(field.label)}
                                                                {field.required === 'True' && (
                                                                    <span className="required-indicator">*</span>
                                                                )}
                                                            </label>
                                                            {renderField(field)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Form Actions */}
                                        <div className="form-actions mt-5 pt-4 border-top">
                                            <div className="row">
                                                <div className="col-12 d-flex gap-2 justify-content-center">
                                                    <button 
                                                        className="btn btn-secondary" 
                                                        type="button"
                                                        onClick={() => navigate(`/${resource}`)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary btn-lg" 
                                                        type="submit"
                                                        disabled={submitting}
                                                    >
                                                        {submitting ? (
                                                            <>
                                                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                                                {local_id === 'new' ? 'Create' : 'Update'}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </main>

            {/* Error Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Header closeButton className="border-danger">
                    <Modal.Title className="text-danger">
                        <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                        Error
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{modalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => { setShowSuccessModal(false); navigate(`/${resource}`); }} centered>
                <Modal.Header closeButton className="border-success">
                    <Modal.Title className="text-success">
                        Success
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-success mb-0">
                        {modalMessage}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="success" 
                        onClick={() => { setShowSuccessModal(false); navigate(`/${resource}`); }}
                    >
                        Done
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GeneralizedForm;
